import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { pushAddToCart, pushRemoveFromCart } from '@theme/data-layer-helper';
import { morph } from '@theme/morph';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  
  /** @type {Promise<void>} */
  #mutationQueue = Promise.resolve();
  
  /** @type {boolean} */
  #isMutating = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Morph only the inner cart-items-component content while preserving the drawer open state.
   * @param {string} html - The new section HTML for this.sectionId
   */
  #updateCartSectionPreservingDrawerState(html) {
    const drawerSectionId = `${this.sectionId}-cart-drawer`;

    const currentDrawer = document.querySelector(
      `cart-drawer-component[data-section-id="${drawerSectionId}"]`
    );

    let wasOpen = false;
    if (currentDrawer instanceof HTMLElement) {
      const dialog = currentDrawer.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement) {
        wasOpen = dialog.open || dialog.classList.contains('open');
      }
    }

    // Parse the full section HTML returned by the server
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Find the new cart-items-component for this section
    const newCartItems = doc.querySelector(
      `cart-items-component[data-section-id="${this.sectionId}"]`
    );

    // Find the existing cart-items-component in the live DOM
    const currentCartItems = document.querySelector(
      `cart-items-component[data-section-id="${this.sectionId}"]`
    );

    if (currentCartItems && newCartItems) {
      // Morph the entire cart-items-component to get updated totals
      // This includes cart-products AND cart-summary with fresh Liquid values
      morph(currentCartItems, newCartItems);
    } else if (!currentCartItems && newCartItems) {
      // Edge case: no current cart-items-component (shouldn't happen with your Liquid)
      const drawerInner = currentDrawer?.querySelector('.cart-drawer__inner');
      if (drawerInner) {
        drawerInner.appendChild(newCartItems);
      }
    } else if (currentCartItems && !newCartItems) {
      // Edge case: server returned no cart-items-component (fallback: clear)
      currentCartItems.innerHTML = '';
    }

    // If the drawer was open before, ensure it stays open
    if (wasOpen && currentDrawer instanceof HTMLElement) {
      // We DIDN'T replace the dialog, so it should already be open.
      // But if your dialog uses classes for open state, ensure they remain.
      const dialog = currentDrawer.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement) {
        if (!dialog.open) {
          // Force it open without re-animating if possible
          dialog.showModal?.();
          dialog.classList.add('open');
        } else {
          dialog.classList.add('open');
        }
      }
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // Prevent concurrent mutations - critical for iOS Safari rapid taps
    if (this.#isMutating) {
      console.warn('Cart mutation already in progress, queuing removal');
    }

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // Set flag to indicate cart is updating (prevents carousel reset)
    (/** @type {any} */ (window)).isCartUpdating = true;

    // Check if this is the last item in the cart
    const isLastItem = this.refs.cartItemRows.length === 1;

    // If removing the last item, show loading state
    if (isLastItem) {
      this.#showCartLoadingState();
    }

    let metafields = {};
    try {
      const trackingScript = cartItemRowToRemove.querySelector('script[cart-item-tracking-data]');
      if (trackingScript) {
        const itemData = JSON.parse(trackingScript.textContent);
        metafields = itemData.metafields || {};
      }
    } catch (e) {
      console.error('Failed to parse cart item tracking data:', e);
    }

    fetch(`${Theme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const cartItem = cart.items[line - 1];
        if (cartItem) {
          pushRemoveFromCart(cartItem, metafields);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch cart for dataLayer:', error);
      });

    // Queue the mutation to prevent race conditions
    this.#queueMutation(() => this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    }));

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Queues a mutation to prevent race conditions when multiple operations happen rapidly.
   * Critical for iOS Safari where users may tap multiple remove buttons quickly.
   * @param {() => Promise<void>} mutationFn - The mutation function to queue
   * @returns {Promise<void>}
   */
  #queueMutation(mutationFn) {
    this.#mutationQueue = this.#mutationQueue
      .then(() => mutationFn())
      .catch((error) => {
        console.error('Queued mutation failed:', error);
      });
    return this.#mutationQueue;
  }

  /**
   * Updates the quantity.
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    // Set mutation flag to prevent concurrent operations
    this.#isMutating = true;
    this.#disableCartItems();

    const { line, quantity } = config;

    // Fetch cart before update to get old quantity and item data for dataLayer
    let oldCartItem = null;
    let oldQuantity = 0;
    let metafields = {};
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      oldCartItem = cart.items[line - 1];
      oldQuantity = oldCartItem?.quantity || 0;

      // Get metafields from tracking script
      const cartItemRow = this.refs.cartItemRows[line - 1];
      const trackingScript = cartItemRow?.querySelector('script[cart-item-tracking-data]');
      if (trackingScript) {
        const itemData = JSON.parse(trackingScript.textContent);
        metafields = itemData.metafields || {};
      }
    } catch (e) {
      console.error('Failed to fetch cart for dataLayer:', e);
    }
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount =
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        // Fire dataLayer events based on quantity change
        // Skip if quantity is 0 (full removal) - that's handled by onLineItemRemove
        if (oldCartItem && quantity !== oldQuantity && quantity > 0) {
          const quantityDiff = Math.abs(quantity - oldQuantity);
          // Create a cart item object with the quantity difference for accurate tracking
          const cartItemForTracking = { ...oldCartItem, quantity: quantityDiff };

          if (quantity > oldQuantity) {
            // Quantity increased - fire add_to_cart
            pushAddToCart(cartItemForTracking, metafields);
          } else if (quantity < oldQuantity) {
            // Quantity decreased - fire remove_from_cart
            pushRemoveFromCart(cartItemForTracking, metafields);
          }
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // Morph only the cart-items-component subtree for this section
        this.#updateCartSectionPreservingDrawerState(
          parsedResponseText.sections[this.sectionId]
        );

        // Also fetch and update cart totals from the server
        // The section response includes updated cart.total_price values
        this.#updateCartTotalsFromResponse(parsedResponseText);

        // Update cart limit message visibility after cart updates
        this.#updateCartLimitVisibility();

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        this.#hideCartLoadingState();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        
        // Clear mutation flags
        this.#isMutating = false;
        (/** @type {any} */ (window)).isCartUpdating = false;
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    this.#hideCartLoadingState();
    
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    // Remove loading class from ALL quantity selectors when any error is shown
    document.querySelectorAll('cart-quantity-selector-component').forEach(selector => {
      selector.classList.remove('quantity-selector--loading');
    });

    // Check if user is trying to add an 11th item (cart has 10 items)
    const cartItemCount = parseInt(document.querySelector('[ref="cartItemCount"]')?.textContent || '0');
    if (cartItemCount === 10) {
      // Show the 10-item limit message when attempting to add 11th item
      this.#showCartLimitMessage();
    } else {
      // Show item-level error for other errors
      this.#updateCartLimitMessage(parsedResponseText.errors);
    }
  };

  #updateCartLimitMessage = (/** @type {string} */ errorMessage) => {
    const limitMessageContainer = /** @type {HTMLElement | null} */ (document.querySelector('.cart-limit-message-container'));
    const limitMessageText = limitMessageContainer?.querySelector('p');
    
    if (limitMessageContainer && limitMessageText) {
      // Show the error message
      limitMessageText.textContent = errorMessage;
      limitMessageContainer.style.display = 'flex';
      
      // Clear the error after 5 seconds and restore original logic
      setTimeout(() => {
        this.#restoreLimitMessageState();
      }, 5000);
    }
  };

  #showCartLimitMessage = () => {
    const limitMessageContainer = /** @type {HTMLElement | null} */ (document.querySelector('.cart-limit-message-container'));
    const limitMessageText = limitMessageContainer?.querySelector('p');
    
    if (limitMessageContainer && limitMessageText) {
      // Show the 10-item limit message
      limitMessageText.textContent = limitMessageText.getAttribute('data-default-message') || 
        "Wow! You've reached the 10 product limit, that's commendable. Start another order to purchase more products.";
      limitMessageContainer.style.display = 'flex';
    }
  };

  #restoreLimitMessageState = () => {
    const limitMessageContainer = /** @type {HTMLElement | null} */ (document.querySelector('.cart-limit-message-container'));
    
    if (limitMessageContainer) {
      // Always hide the limit message when restoring state
      limitMessageContainer.style.display = 'none';
    }
  };

  // Call this method when cart updates to ensure proper visibility state
  #updateCartLimitVisibility = () => {
    this.#restoreLimitMessageState();
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      // Morph only the cart-items-component subtree for this section
      this.#updateCartSectionPreservingDrawerState(cartItemsHtml);

      this.#updateCartQuantitySelectorButtonStates();
      
      // Update cart limit message visibility after cart updates
      this.#updateCartLimitVisibility();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #showCartLoadingState() {
    // Add loading class to the cart items component
    this.classList.add('cart-items-loading');
  }

  #hideCartLoadingState() {
    // Remove loading class
    this.classList.remove('cart-items-loading');
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if (
          'updateCartQuantity' in selector &&
          typeof selector.updateCartQuantity === 'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll(
      'cart-quantity-selector-component'
    );
    for (const selector of cartQuantitySelectors) {
      if (
        'updateButtonStates' in selector &&
        typeof selector.updateButtonStates === 'function'
      ) {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Updates cart totals from the cart/change.js response.
   * Critical for iOS Safari where totals don't update after removal.
   * @param {Object} response - The parsed response from cart/change.js
   */
  async #updateCartTotalsFromResponse(response) {
    try {
      // The section response already contains the updated cart summary with Shopify-formatted prices
      // Just need to extract and update the DOM elements
      const sectionHTML = response.sections?.[this.sectionId];
      if (!sectionHTML) return;

      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      
      // Extract the formatted subtotal from the section response
      const newSubtotal = doc.querySelector('.cart-subtotal dd');
      const currentSubtotal = document.querySelector('.cart-subtotal dd');
      if (newSubtotal && currentSubtotal) {
        currentSubtotal.innerHTML = newSubtotal.innerHTML;
      }

      // Extract the formatted estimated total from the section response
      const newEstimatedTotal = doc.querySelector('.cart-estimated-total dd');
      const currentEstimatedTotal = document.querySelector('.cart-estimated-total dd');
      if (newEstimatedTotal && currentEstimatedTotal) {
        currentEstimatedTotal.innerHTML = newEstimatedTotal.innerHTML;
      }

      // Update Klarna purchase amount if present
      const newKlarna = doc.querySelector('klarna-placement');
      const currentKlarna = document.querySelector('klarna-placement');
      if (newKlarna && currentKlarna) {
        const purchaseAmount = newKlarna.getAttribute('data-purchase-amount');
        if (purchaseAmount) {
          currentKlarna.setAttribute('data-purchase-amount', purchaseAmount);
        }
      }
    } catch (error) {
      console.error('Failed to update cart totals:', error);
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
